import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  dismissNotifications,
  fetchNotifications,
  markNotificationsRead,
} from "../helpers/apiHelper";
import { countUnread, normalizeNotificationsResponse } from "../helpers/notificationsHelper";
import { useAuth } from "./AuthContext";
import { useTranslation } from "./TranslationContext";

export const NOTIFICATIONS_STATUS = Object.freeze({
  IDLE: "idle",
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
});

const noop = () => {};

// A background refresh right after a completed fetch (e.g. login then dashboard) is skipped.
export const BACKGROUND_REFRESH_MIN_AGE_MS = 10000;

const NotificationsContext = createContext({
  notifications: [],
  unreadCount: 0,
  status: NOTIFICATIONS_STATUS.IDLE,
  // True once GET /notifications has answered (or failed) for the current login.
  settled: false,
  refresh: noop,
  refreshInBackground: noop,
  markRead: noop,
  markAllRead: noop,
  dismiss: noop,
});

export const NotificationsProvider = ({ children }) => {
  const { tokens, user, loading: authLoading } = useAuth();
  const { language } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [status, setStatus] = useState(NOTIFICATIONS_STATUS.IDLE);
  const [settledSessionKey, setSettledSessionKey] = useState(null);
  const requestRef = useRef(null);
  const lastLoadedRef = useRef(null);
  const tokenRef = useRef(tokens?.idToken);
  const notificationsRef = useRef(notifications);
  // Local writes win over a GET that was answered before the POST landed.
  const localReadRef = useRef(new Set());
  const localDismissedRef = useRef(new Set());
  const sessionKey =
    user?.portalUserId || user?.userId || user?.cognitoUserId || user?.email;
  const isAuthenticated = Boolean(tokens?.idToken && sessionKey);

  tokenRef.current = tokens?.idToken;
  notificationsRef.current = notifications;

  const load = useCallback(() => {
    const token = tokenRef.current;
    if (!token || !sessionKey) return;

    // Background refreshes share the request already in flight for this session/language.
    const inFlight = requestRef.current;
    if (inFlight && inFlight.sessionKey === sessionKey && inFlight.language === language) return;

    inFlight?.controller.abort();
    const controller = new AbortController();
    requestRef.current = { controller, sessionKey, language };
    setStatus(NOTIFICATIONS_STATUS.LOADING);

    fetchNotifications(token, language, controller.signal)
      .then((response) => {
        if (requestRef.current?.controller !== controller) return;
        setNotifications(
          normalizeNotificationsResponse(response).map((item) => {
            if (localDismissedRef.current.has(item.id)) {
              return { ...item, read: true, dismissed: true, showOnLogin: false };
            }
            return localReadRef.current.has(item.id) ? { ...item, read: true } : item;
          }),
        );
        lastLoadedRef.current = { sessionKey, language, at: Date.now() };
        setStatus(NOTIFICATIONS_STATUS.READY);
      })
      .catch(() => {
        if (requestRef.current?.controller !== controller) return;
        // A failed refresh keeps whatever this session already loaded.
        setStatus(NOTIFICATIONS_STATUS.ERROR);
      })
      .finally(() => {
        if (requestRef.current?.controller === controller) {
          requestRef.current = null;
          setSettledSessionKey(sessionKey);
        }
      });
  }, [language, sessionKey]);

  const refreshInBackground = useCallback(() => {
    const last = lastLoadedRef.current;
    if (
      last
      && last.sessionKey === sessionKey
      && last.language === language
      && Date.now() - last.at < BACKGROUND_REFRESH_MIN_AGE_MS
    ) return;
    load();
  }, [language, load, sessionKey]);

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      requestRef.current?.controller.abort();
      requestRef.current = null;
      setNotifications([]);
      setStatus(NOTIFICATIONS_STATUS.IDLE);
      setSettledSessionKey(null);
      lastLoadedRef.current = null;
      localReadRef.current = new Set();
      localDismissedRef.current = new Set();
      return;
    }

    load();
  }, [authLoading, isAuthenticated, load]);

  useEffect(() => {
    if (settledSessionKey && settledSessionKey !== sessionKey) {
      localReadRef.current = new Set();
      localDismissedRef.current = new Set();
      setNotifications([]);
      setSettledSessionKey(null);
    }
  }, [sessionKey, settledSessionKey]);

  useEffect(() => () => requestRef.current?.controller.abort(), []);

  const updateItems = useCallback((ids, patch) => {
    const idSet = new Set(ids);
    setNotifications((current) =>
      current.map((item) => (idSet.has(item.id) ? { ...item, ...patch } : item)),
    );
  }, []);

  const markRead = useCallback(
    (ids) => {
      const unread = new Set(
        notificationsRef.current.filter((item) => !item.read).map((item) => item.id),
      );
      const pending = (ids || []).filter((id) => unread.has(id));
      if (!pending.length || !tokenRef.current) return;
      pending.forEach((id) => localReadRef.current.add(id));
      updateItems(pending, { read: true });
      markNotificationsRead(tokenRef.current, pending).catch(() => {
        // The next GET /notifications reconciles a failed write.
      });
    },
    [updateItems],
  );

  const markAllRead = useCallback(() => {
    markRead(notificationsRef.current.map((item) => item.id));
  }, [markRead]);

  const dismiss = useCallback(
    (ids) => {
      const known = new Set(notificationsRef.current.map((item) => item.id));
      const pending = (ids || []).filter((id) => known.has(id));
      if (!pending.length || !tokenRef.current) return;
      pending.forEach((id) => localDismissedRef.current.add(id));
      updateItems(pending, { read: true, dismissed: true, showOnLogin: false });
      dismissNotifications(tokenRef.current, pending).catch(() => {
        // The next GET /notifications reconciles a failed write.
      });
    },
    [updateItems],
  );

  const settled = isAuthenticated && settledSessionKey === sessionKey;
  const unreadCount = countUnread(notifications);
  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      status,
      settled,
      refresh: load,
      refreshInBackground,
      markRead,
      markAllRead,
      dismiss,
    }),
    [
      notifications,
      unreadCount,
      status,
      settled,
      load,
      refreshInBackground,
      markRead,
      markAllRead,
      dismiss,
    ],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationsContext);
